import React, { useState, useEffect } from "react";
import Select from "react-select";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterMomentJalaali } from "@mui/x-date-pickers/AdapterMomentJalaali";
import moment from "moment-jalaali";
import { TextField } from "@mui/material";
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FaCar, FaChartBar, FaClock, FaRoute } from "react-icons/fa";
import "./ReportPage.css";

moment.loadPersian({ dialect: "persian-modern", usePersianDigits: true });

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const FitBounds = ({ positions }) => {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 1) {
      map.fitBounds(positions, { padding: [40, 40] });
    } else if (positions.length === 1) {
      map.setView(positions[0], 15);
    }
  }, [positions, map]);
  return null;
};

const ReportPage = () => {
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [reportType, setReportType] = useState(null);
  const [timeRange, setTimeRange] = useState(null);
  const [isCustom, setIsCustom] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(moment().startOf("day"));
  const [customEndDate, setCustomEndDate] = useState(moment().endOf("day"));
  const [routePositions, setRoutePositions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDevices = async () => {
    try {
      const response = await fetch("/api/devices", {
        method: "GET",
        headers: { accept: "application/json" },
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setDevices(data);
        if (data.length === 1) {
          setSelectedDevice({ value: data[0].id, label: data[0].name });
        }
      }
    } catch (error) {
      console.error("خطا در دریافت دستگاه‌ها:", error);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const deviceOptions = devices.map((d) => ({ value: d.id, label: d.name }));

  const reportOptions = [
    { value: "route",    label: "مسیر" },
    { value: "trips",    label: "سفرها" },
    { value: "stops",    label: "توقف‌ها" },
    { value: "summary",  label: "خلاصه" },
    { value: "events",   label: "رویدادها" },
    { value: "combined", label: "ترکیبی" },
  ];

  const timeOptions = [
    { value: "today",     label: "امروز" },
    { value: "yesterday", label: "دیروز" },
    { value: "week",      label: "این هفته" },
    { value: "month",     label: "این ماه" },
    { value: "custom",    label: "سفارشی" },
  ];

  const handleTimeRangeChange = (selected) => {
    setTimeRange(selected);
    setIsCustom(selected.value === "custom");
  };

  const getDateRange = () => {
    if (isCustom) {
      return {
        from: customStartDate.clone().startOf("day").toISOString(),
        to: customEndDate.clone().endOf("day").toISOString(),
      };
    }
    const now = moment();
    switch (timeRange.value) {
      case "today":
        return { from: now.clone().startOf("day").toISOString(), to: now.clone().endOf("day").toISOString() };
      case "yesterday":
        return { from: now.clone().subtract(1,"day").startOf("day").toISOString(), to: now.clone().subtract(1,"day").endOf("day").toISOString() };
      case "week":
        return { from: now.clone().startOf("week").toISOString(), to: now.clone().endOf("week").toISOString() };
      case "month":
        return { from: now.clone().startOf("month").toISOString(), to: now.clone().endOf("month").toISOString() };
      default: return null;
    }
  };

  const generateReport = async () => {
    if (!selectedDevice || !reportType || !timeRange) {
      alert("لطفاً همه گزینه‌ها را انتخاب کنید.");
      return;
    }
    const dateRange = getDateRange();
    if (!dateRange) return;
    setRoutePositions([]);

    if (reportType.value === "route") {
      setIsLoading(true);
      try {
        const url = `/api/reports/route?deviceId=${selectedDevice.value}&from=${encodeURIComponent(dateRange.from)}&to=${encodeURIComponent(dateRange.to)}`;
        const response = await fetch(url, {
          method: "GET",
          headers: { accept: "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json();
          if (Array.isArray(data) && data.length > 0) {
            const coords = data.filter((p) => p.latitude && p.longitude).map((p) => [p.latitude, p.longitude]);
            setRoutePositions(coords);
          } else {
            alert("مسیری برای این بازه زمانی یافت نشد.");
          }
        } else {
          alert("خطا در دریافت مسیر.");
        }
      } catch (error) {
        console.error("خطا:", error);
        alert("خطا در اتصال به سرور.");
      } finally {
        setIsLoading(false);
      }
    } else {
      alert("نوع گزارش «" + reportType.label + "» در حال توسعه است.");
    }
  };

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: 10,
      border: "1.5px solid #e2e8f0",
      boxShadow: "none",
      fontSize: 14,
      "&:hover": { borderColor: "#1a73e8" },
    }),
    option: (base, state) => ({
      ...base,
      textAlign: "right",
      direction: "rtl",
      background: state.isSelected ? "#1a73e8" : state.isFocused ? "#ebf4ff" : "#fff",
      color: state.isSelected ? "#fff" : "#333",
    }),
  };

  return (
    <div className={`report-page ${routePositions.length > 0 ? "has-map" : ""}`}>
      <div className="report-header">
        <h2>گزارش‌گیری</h2>
        <p>دستگاه، نوع گزارش و بازه زمانی را انتخاب کنید</p>
      </div>

      <div className="report-body">
        {devices.length !== 1 && (
          <div className="field-card">
            <div className="field-card-label">
              <span className="field-card-icon icon-device"><FaCar /></span>
              انتخاب دستگاه
            </div>
            <Select
              options={deviceOptions}
              placeholder="یک دستگاه انتخاب کنید..."
              onChange={setSelectedDevice}
              styles={selectStyles}
            />
          </div>
        )}

        {devices.length === 1 && (
          <div className="field-card single-device-badge">
            <div className="field-card-label">
              <span className="field-card-icon icon-device"><FaCar /></span>
              دستگاه: <span className="single-device-name">{devices[0].name}</span>
            </div>
          </div>
        )}

        <div className="field-card">
          <div className="field-card-label">
            <span className="field-card-icon icon-report"><FaChartBar /></span>
            نوع گزارش
          </div>
          <Select
            options={reportOptions}
            placeholder="نوع گزارش را انتخاب کنید..."
            onChange={setReportType}
            styles={selectStyles}
          />
        </div>

        <div className="field-card">
          <div className="field-card-label">
            <span className="field-card-icon icon-time"><FaClock /></span>
            بازه زمانی
          </div>
          <Select
            options={timeOptions}
            placeholder="یک بازه زمانی انتخاب کنید..."
            onChange={handleTimeRangeChange}
            styles={selectStyles}
          />
        </div>

        {isCustom && (
          <div className="custom-time-wrapper">
            <LocalizationProvider dateAdapter={AdapterMomentJalaali}>
              <div className="custom-time-fields">
                <DateTimePicker
                  label="تاریخ و ساعت شروع"
                  value={customStartDate}
                  onChange={(v) => v && setCustomStartDate(v)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                <DateTimePicker
                  label="تاریخ و ساعت پایان"
                  value={customEndDate}
                  onChange={(v) => v && setCustomEndDate(v)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </div>
            </LocalizationProvider>
          </div>
        )}

        <div className="report-actions">
          <button className="generate-report-button" onClick={generateReport} disabled={isLoading}>
            {isLoading ? "در حال بارگذاری مسیر..." : "دریافت گزارش"}
          </button>
        </div>

        {routePositions.length > 0 && (
          <div className="route-map-container">
            <h3 className="route-map-title">
              <FaRoute /> مسیر حرکت — {selectedDevice?.label}
            </h3>
            <MapContainer center={routePositions[0]} zoom={13} className="route-map">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              <Polyline positions={routePositions} color="#1a73e8" weight={4} opacity={0.85} />
              <Marker position={routePositions[0]}>
                <Popup>شروع مسیر</Popup>
              </Marker>
              <Marker position={routePositions[routePositions.length - 1]}>
                <Popup>پایان مسیر</Popup>
              </Marker>
              <FitBounds positions={routePositions} />
            </MapContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPage;
